import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Lock, LogOut, Crown, RefreshCw, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

import { AdminProvider } from './admin/AdminContext';
import DashboardTab from './admin/DashboardTab';
import TrafficTab from './admin/TrafficTab';
import UsageTab from './admin/UsageTab';
import CostsTab from './admin/CostsTab';
import UsersTab from './admin/UsersTab';
import TrainingTab from './admin/TrainingTab';
import ExercisesTab from './admin/ExercisesTab';
import ActivityTab from './admin/ActivityTab';
import EmailTab from './admin/EmailTab';
import AuditTab from './admin/AuditTab';
import AlertsTab from './admin/AlertsTab';
import SettingsTab from './admin/SettingsTab';
import RetentionTab from './admin/RetentionTab';

const API_URL = '';
const APP_VERSION = '3.14';

export default function AdminPanel() {
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const isAuth = localStorage.getItem('gym_notebook_admin_authenticated') === 'true';
    if (isAuth && !sessionStorage.getItem('admin_password')) {
      localStorage.removeItem('gym_notebook_admin_authenticated');
      return false;
    }
    return isAuth;
  });
  const [password, setPassword] = useState(() => sessionStorage.getItem('admin_password') || '');
  const [showPassword, setShowPassword] = useState(false);

  // Shared state
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [stats, setStats] = useState({
    totalUsers: 0, activeUsers: 0, paidUsers: 0, freeUsers: 0,
    trialUsers: 0, totalRevenue: 0, newUsersToday: 0, newUsersThisWeek: 0,
    totalFoodEntries: 0, totalWalkingSessions: 0, totalPhotos: 0
  });
  const [users, setUsers] = useState([]);

  // Admin fetch helper
  const adminFetch = useCallback((url, options = {}) => {
    const adminPwd = sessionStorage.getItem('admin_password') || password;
    return fetch(url, {
      ...options,
      headers: { ...options.headers, 'X-Admin-Password': adminPwd }
    });
  }, [password]);

  // Load shared data
  const loadStats = useCallback(async () => {
    try {
      const response = await adminFetch(`${API_URL}/api/v2/admin/stats`);
      if (response.ok) setStats(await response.json());
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, [adminFetch]);

  const loadUsers = useCallback(async () => {
    try {
      const response = await adminFetch(`${API_URL}/api/v2/admin/users`, {
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      } else {
        toast.error(`Failed to load users: ${response.status}`);
      }
    } catch (error) {
      toast.error('Network error loading users');
    }
  }, [adminFetch]);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadStats(), loadUsers()]);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      toast.error('Failed to refresh data');
    }
    setLoading(false);
  }, [loadStats, loadUsers]);

  // Auth
  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/v2/admin/stats`, {
        headers: { 'X-Admin-Password': password }
      });
      if (response.ok) {
        setIsAuthenticated(true);
        localStorage.setItem('gym_notebook_admin_authenticated', 'true');
        sessionStorage.setItem('admin_password', password);
        toast.success('Admin access granted');
      } else {
        toast.error('Invalid password');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('gym_notebook_admin_authenticated');
    sessionStorage.removeItem('admin_password');
    setIsAuthenticated(false);
    setPassword('');
    toast.success('Logged out');
  };

  useEffect(() => {
    if (isAuthenticated) {
      refreshAll();
    }
  }, [isAuthenticated]);

  // User actions
  const extendSubscription = async (userId, days, type = 'trial') => {
    try {
      const response = await adminFetch(`${API_URL}/api/v2/admin/subscription/extend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Password': password },
        body: JSON.stringify({ user_id: userId, days, type })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Extended ${type} by ${days} days until ${new Date(data.subscription_end).toLocaleDateString()}`);
        loadUsers(); loadStats();
      } else {
        toast.error('Failed to extend subscription');
      }
    } catch (error) {
      toast.error('Error extending subscription');
    }
  };

  const extendUserTrial = async (userId) => {
    const adminPwd = sessionStorage.getItem('admin_password') || password;
    if (!adminPwd) {
      toast.error('Session expired. Please log in again.');
      setIsAuthenticated(false);
      localStorage.removeItem('gym_notebook_admin_authenticated');
      return;
    }
    try {
      const response = await adminFetch(`${API_URL}/api/v2/admin/users/${userId}/extend-trial`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Password': adminPwd },
        body: JSON.stringify({ days: 14 })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Extended trial by 14 days. New end: ${new Date(data.new_end_date).toLocaleDateString()}`);
        loadUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to extend trial');
      }
    } catch (error) {
      toast.error('Error extending trial');
    }
  };

  const exportUsersCSV = () => {
    const headers = ['Name', 'Email', 'Registered', 'Last Login', 'Workouts', 'Status'];
    const rows = users.map(u => [
      u.name, u.email,
      new Date(u.created_at).toLocaleDateString(),
      u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never',
      u.total_workouts || 0,
      u.is_paid ? 'Paid' : 'Free'
    ]);
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Users exported to CSV');
  };

  const deleteTestUsers = async () => {
    if (!window.confirm('Are you sure you want to delete ALL test users? This cannot be undone.')) return;
    try {
      const response = await adminFetch(`${API_URL}/api/v2/admin/delete-test-users`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        loadUsers(); loadStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete test users');
      }
    } catch (error) {
      toast.error('Error deleting test users');
    }
  };

  const clearTestData = async () => {
    if (!window.confirm('Are you sure you want to clear ALL test data? This cannot be undone.')) return;
    try {
      const response = await adminFetch(`${API_URL}/api/v2/admin/clear-test-data`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        refreshAll();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to clear test data');
      }
    } catch (error) {
      toast.error('Error clearing test data');
    }
  };

  // Context value
  const contextValue = { adminFetch, API_URL };

  // --- LOGIN SCREEN ---
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="bg-slate-800 border-slate-700 w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mb-4">
              <Lock className="h-8 w-8 text-orange-400" />
            </div>
            <CardTitle className="text-2xl text-white">Admin Access</CardTitle>
            <CardDescription className="text-slate-400">Enter admin password to continue</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label className="text-slate-300">Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter admin password"
                    className="bg-slate-700 border-slate-600 text-white pr-10"
                    autoFocus
                    data-testid="admin-password-input"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-400" data-testid="admin-login-btn">
                <Lock className="h-4 w-4 mr-2" />Access Admin Panel
              </Button>
            </form>
            <p className="text-center text-slate-500 text-xs mt-4">Walking & Nutrition Notebook v{APP_VERSION}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // --- ADMIN DASHBOARD ---
  const tabs = ['dashboard', 'traffic', 'usage', 'costs', 'users', 'retention', 'activity', 'audit', 'alerts', 'email', 'settings'];

  return (
    <AdminProvider value={contextValue}>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        {/* Header */}
        <header className="bg-slate-800/50 border-b border-slate-700 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                <Crown className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Admin Panel</h1>
                <p className="text-xs text-slate-400">Walking & Nutrition Notebook</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={refreshAll} disabled={loading}
                className="border-slate-600 text-slate-300" data-testid="admin-refresh-btn">
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
              </Button>
              <span className="text-xs text-slate-500">{users.length} users</span>
              <Button variant="ghost" size="sm" onClick={handleLogout}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/20" data-testid="admin-logout-btn">
                <LogOut className="h-4 w-4 mr-2" />Logout
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="max-w-7xl mx-auto px-4 pb-2">
            <div className="flex gap-1 overflow-x-auto">
              {tabs.map(tab => (
                <button key={tab} onClick={() => setActiveTab(tab)} data-testid={`admin-tab-${tab}`}
                  className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab
                      ? 'bg-slate-700 text-white'
                      : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                  }`}>
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 py-6">
          {activeTab === 'dashboard' && <DashboardTab stats={stats} refreshTrigger={refreshTrigger} />}
          {activeTab === 'traffic' && <TrafficTab refreshTrigger={refreshTrigger} />}
          {activeTab === 'usage' && <UsageTab refreshTrigger={refreshTrigger} />}
          {activeTab === 'costs' && <CostsTab refreshTrigger={refreshTrigger} />}
          {activeTab === 'users' && (
            <UsersTab
              users={users} stats={stats}
              onExtendSubscription={extendSubscription}
              onExtendUserTrial={extendUserTrial}
              onDeleteUser={() => { loadUsers(); loadStats(); }}
              onExportCSV={exportUsersCSV}
              onLoadUsers={loadUsers}
              onLoadStats={loadStats}
            />
          )}
          {activeTab === 'training' && <TrainingTab />}
          {activeTab === 'exercises' && <ExercisesTab refreshTrigger={refreshTrigger} />}
          {activeTab === 'activity' && <ActivityTab refreshTrigger={refreshTrigger} />}
          {activeTab === 'email' && <EmailTab stats={stats} refreshTrigger={refreshTrigger} />}
          {activeTab === 'audit' && <AuditTab refreshTrigger={refreshTrigger} />}
          {activeTab === 'alerts' && <AlertsTab refreshTrigger={refreshTrigger} />}
          {activeTab === 'retention' && <RetentionTab refreshTrigger={refreshTrigger} />}
          {activeTab === 'settings' && <SettingsTab onDeleteTestUsers={deleteTestUsers} onClearTestData={clearTestData} />}
        </main>
      </div>
    </AdminProvider>
  );
}
